import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download } from "lucide-react";
import { getSpecies, SPECIES_CATALOG } from "../data/species";
import { localizeSpecies } from "../data/speciesTranslations";
import { fadeUp, stagger } from "../components/motion";
import { BROCHURE_URL } from "../lib/api";
import { SpeciesGallery } from "../components/SpeciesGallery";
import { LangToggle } from "../components/Navigation";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n";

const Spec = ({ label, value }) => (
  <div className="border-t border-border py-4">
    <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-timber">{label}</dt>
    <dd className="mt-1 font-serif text-2xl text-brand-blue">{value}</dd>
  </div>
);

export default function SpeciesDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const species = localizeSpecies(getSpecies(slug), lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!species) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-brand-bone px-6 text-center">
        <h1 className="font-serif text-5xl text-brand-blue">{t("detail.notFound")}</h1>
        <Link to="/#species" data-testid="notfound-back" className="rounded-full bg-brand-blue px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-bone">
          {t("detail.back")}
        </Link>
      </div>
    );
  }

  const related = SPECIES_CATALOG.filter((s) => s.slug !== species.slug && s.type === species.type).slice(0, 3);
  const typeWord = t(`detail.type${species.type}`);

  return (
    <div className="bg-brand-bone" data-testid="species-detail">
      <Seo
        title={`${species.name} (${species.latin}) — 7 Seas Group USA`}
        description={`Export-grade ${species.name} from 7 Seas Group USA. ${species.note} Grades: ${species.grades.join(", ")}.`}
        image={species.gallery?.[0]?.src || species.image}
      />
      {/* Top bar */}
      <div className="fixed top-0 left-0 z-50 w-full border-b border-border bg-brand-bone/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-12">
          <button onClick={() => navigate("/")} data-testid="detail-logo" className="flex items-center">
            <img src="/logo.png" alt="7 Seas Group USA" className="h-10 w-auto" />
          </button>
          <div className="flex items-center gap-4">
            <LangToggle className="hidden sm:flex" />
            <button
              onClick={() => navigate("/#species")}
              data-testid="detail-back-btn"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand-blue transition-colors hover:text-brand-timber"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              {t("detail.allSpecies")}
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative flex min-h-[80vh] items-end overflow-hidden pt-24">
        <motion.img
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          src={species.image}
          alt={species.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/40 to-brand-blue/10" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-16 md:px-12">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={fadeUp} className="mb-4 flex items-center gap-4">
              <span className="rounded-full border border-brand-bone/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bone">{species.type}</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-bone/80">{species.origin}</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-serif text-6xl font-medium leading-[0.9] tracking-tight text-brand-bone md:text-8xl">
              {species.name}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-2 font-mono text-sm italic text-brand-bone/70">
              {species.latin}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-24 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">{t("detail.overview")}</span>
          <p className="mt-6 font-light text-xl leading-relaxed text-brand-slate md:text-2xl">
            {species.description}
          </p>

          <div className="mt-14 grid grid-cols-1 gap-x-12 sm:grid-cols-2">
            <div>
              <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">{t("detail.grades")}</h3>
              <ul className="mb-8">
                {species.grades.map((g) => (
                  <li key={g} className="border-b border-border py-3 font-serif text-2xl text-brand-blue">{g}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">{t("detail.dims")}</h3>
              <ul className="mb-8">
                {species.dimensions.map((d) => (
                  <li key={d} className="border-b border-border py-3 font-serif text-2xl text-brand-blue">{d}</li>
                ))}
              </ul>
            </div>
          </div>

          <h3 className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">{t("detail.uses")}</h3>
          <div className="flex flex-wrap gap-3">
            {species.uses.map((u) => (
              <span key={u} className="rounded-full border border-brand-blue/20 px-4 py-2 font-mono text-xs uppercase tracking-wider text-brand-blue">{u}</span>
            ))}
          </div>
        </div>

        {/* Sidebar specs */}
        <aside className="lg:col-span-4 lg:col-start-9">
          <div className="lg:sticky lg:top-28">
            <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">{t("detail.chars")}</h3>
            <dl>
              <Spec label={t("detail.colour")} value={species.properties.color} />
              <Spec label={t("detail.density")} value={species.properties.density} />
              <Spec label={t("detail.hardness")} value={species.properties.hardness} />
              <Spec label={t("detail.workability")} value={species.properties.workability} />
            </dl>
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => navigate("/#quote")}
                data-testid="detail-quote-btn"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand-blue px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-bone transition-colors duration-300 hover:bg-brand-timber"
              >
                {t("detail.quoteBtn")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={BROCHURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-brochure-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-blue/30 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-blue transition-colors duration-300 hover:border-brand-timber hover:text-brand-timber"
              >
                <Download size={14} /> {t("nav.downloadBrochure")}
              </a>
            </div>
          </div>
        </aside>
      </section>

      {/* Swipeable gallery */}
      <SpeciesGallery species={species} />

      {/* Related */}
      <section className="bg-brand-blue py-24 text-brand-bone">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <h2 className="mb-12 font-serif text-4xl font-medium md:text-5xl">
            {t("detail.more").replace("{type}", typeWord)}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/species/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group relative overflow-hidden rounded-sm border border-brand-bone/15"
              >
                <img src={r.image} alt={r.name} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/90 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-bone/70">{r.origin}</p>
                  <h3 className="font-serif text-3xl">{r.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
